import { type NodeProps, useReactFlow } from '@xyflow/react';
import React, { useCallback, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { ColorPicker } from '../../../common/color-picker/ColorPicker';
import { RfNodeStickyNote, SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { RichTextEditorProvider } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { RichTextEditor } from '../../../common/rich-text-editor/RichTextEditor';
import { RichTextToolbar } from '../../../common/rich-text-editor/RichTextToolbar';
import { SmallColorSwatchButton } from '../../../common/color-picker/SmallColorSwatchButton';
import { CanvasNodeToolbar } from '../components/CanvasNodeToolbar';
import { StickyNodeDuplicators } from './StickyNodeDuplicators';
import { NodeActionsMenu } from '../components/NodeActionsMenu';
import { useNodeRichTextEditBehavior } from '../hooks/useNodeRichTextEditBehavior';
import { useNodeDeleteOnKeyPress } from '../hooks/useNodeDeleteOnKeyPress';
import { useNodeHeightFromContentBehavior } from '../hooks/useNodeHeightFromContentBehavior';
import { Separator } from '../../../common/rich-text-editor/toolbar-items/Separator';
import { useRichTextEditor } from '../../../common/rich-text-editor/RichTextEditorProvider';
import {
  STICKY_NOTE_DEFAULT_HEIGHT,
  STICKY_NOTE_MIN_HEIGHT,
  STICKY_NOTE_DEFAULT_WIDTH,
} from './consts';
import { SelectiveAspectRatioResizer } from '../components/selective-aspect-ration-resizer/SelectiveAspectRatioResizer';
import { SelectiveAspectRatioResizerCtxProvider } from '../components/selective-aspect-ration-resizer/SelectiveAspectRatioResizerCtx';
import { useSelectiveAspectRatioResizerCtx } from '../components/selective-aspect-ration-resizer/SelectiveAspectRatioResizerCtx';

import { CSS_Z_INDEX_NODE_HANDLE } from '../../const/zIndex';
import { resizeControlStyles } from '../resizeControlStyles';
import { useNodeTextScaleCtx } from '../../internal-context/NodeTextScaleContext';

const STICKY_NOTE_DEFAULT_COLOR = '#EBDEFF';

const PLACEHOLDER_TEXT = 'Type anything';

const styles = {
  leftToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    alignItems: 'top',
    justifyContent: 'top',
    backgroundColor: 'white',
    padding: '8px',
    border: `1px solid ${colors.grey[300]}`,
    '&.drop-target': {
      borderColor: colors.blue[500],
    },
    borderRadius: 0,
  },
} as const;

const calculateNewTextScale = (
  previousWidth: number,
  newWidth: number,
  previousTextScale: number,
) => {
  return (newWidth / previousWidth) * previousTextScale;
};

const StickyNoteNode = ({
  id,
  selected,
  dragging,
  width = STICKY_NOTE_DEFAULT_WIDTH,
  height,
  data: { color, richText, draft, freshlyCreated, textScale = 1 },
}: NodeProps<RfNodeStickyNote>) => {
  const { updateNodeData } = useReactFlow<SweepCanvasRfNode>();

  const [popoverAnchor, setPopoverAnchor] = useState<(EventTarget & Element) | null>(null);
  const [selectedColor, setSelectedColor] = useState(color ?? STICKY_NOTE_DEFAULT_COLOR);

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
  }, []);

  const handleColorChange = useCallback(
    (newColor: string) => {
      setSelectedColor(newColor);
      updateNodeData(id, { color: newColor });
      handleClosePopover();
    },
    [id, updateNodeData, handleClosePopover],
  );

  const {
    editMode,
    exclusivelySelected,
    setEditMode,
    multipleNodesSelected,
    handleRichTextChange,
    saveRichText,
  } = useNodeRichTextEditBehavior({
    id,
    freshlyCreated,
    selected,
    dragging,
    temporary: draft,
    currentRichText: richText,
  });

  useNodeDeleteOnKeyPress({
    nodeId: id,
    exclusivelySelected,
    editMode,
  });

  const { urlEditorOpen } = useRichTextEditor();

  const maybeSetEditMode = useCallback(() => {
    if (exclusivelySelected) {
      setEditMode(true);
    }
  }, [exclusivelySelected, setEditMode]);

  const { isResizing, initialWidthRef, isResizingHorizontal } = useSelectiveAspectRatioResizerCtx();
  const { nodesTextScaleRef } = useNodeTextScaleCtx();

  const placeholder = draft || !exclusivelySelected ? undefined : PLACEHOLDER_TEXT;

  let _textScale = textScale;
  if (isResizing && !isResizingHorizontal && initialWidthRef.current) {
    const newTextScale = calculateNewTextScale(initialWidthRef.current, width, textScale);
    nodesTextScaleRef.current.set(id, newTextScale);
    _textScale = newTextScale;
  }

  const { boxRef } = useNodeHeightFromContentBehavior({
    ratio: _textScale,
    height: height || STICKY_NOTE_DEFAULT_HEIGHT,
    nodeId: id,
    editMode,
    minHeight: STICKY_NOTE_MIN_HEIGHT * _textScale,
    defaultHeight: STICKY_NOTE_DEFAULT_HEIGHT * _textScale,
  });

  const saveStickyNoteData = useCallback(() => {
    const _textScale = nodesTextScaleRef.current.get(id) ?? textScale;
    saveRichText(_textScale, height);
  }, [nodesTextScaleRef, id, textScale, saveRichText, height]);

  const isResizerVisible = selected && !dragging && !multipleNodesSelected;

  return (
    <>
      {!draft && !editMode && isResizerVisible && (
        <SelectiveAspectRatioResizer
          isVisible={true}
          handleStyle={resizeControlStyles.handle}
          lineStyle={resizeControlStyles.line}
          defaultKeepAspectRatio={true}
          horizontalKeepAspectRatio={false}
          handleClassName="section-node-handle"
          minWidth={STICKY_NOTE_DEFAULT_WIDTH}
          minHeight={STICKY_NOTE_MIN_HEIGHT}
          autoScale
          allowedWidths={[
            STICKY_NOTE_DEFAULT_WIDTH * textScale,
            STICKY_NOTE_DEFAULT_WIDTH * textScale * 1.75,
          ]}
        />
      )}

      {!draft && !isResizing && (
        <CanvasNodeToolbar
          className="nopan"
          align="center"
          isVisible={exclusivelySelected}
          offset={18}
          hide={urlEditorOpen}
        >
          <SmallColorSwatchButton
            color={selectedColor}
            onClick={handleButtonClick}
            pressed={Boolean(popoverAnchor)}
          />
          <Separator />
          <RichTextToolbar />
          <Separator />
          <NodeActionsMenu nodeId={id} />
        </CanvasNodeToolbar>
      )}
      {exclusivelySelected && !isResizing && !editMode && <StickyNodeDuplicators nodeId={id} />}
      <Paper
        sx={{
          ...styles.outerContainer,
          backgroundColor: `${selectedColor}`,
          opacity: draft ? 0.5 : 1,
          border: editMode ? `1px solid ${colors.blue[500]}` : `1px solid ${selectedColor}`,
          cursor: 'move',
        }}
        elevation={draft ? 0 : 1}
        className={!editMode ? 'drag-handle' : undefined}
        onClick={maybeSetEditMode}
      >
        <Box
          ref={boxRef}
          sx={{
            cursor: 'text',
            position: 'absolute',
            top: 0,
            left: 0,
            scale: _textScale,
            transformOrigin: 'top left',
            width: `${width / _textScale}px`,
            padding: '12px',
          }}
        >
          <RichTextEditor
            onChange={handleRichTextChange}
            onBlur={saveStickyNoteData}
            initialContent={richText}
            placeholder={placeholder}
            autoFocus
            hideOutline
            editable={editMode}
          />
          {!editMode && (
            <Box
              className="drag-handle"
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: CSS_Z_INDEX_NODE_HANDLE,
                cursor: 'move',
              }}
              onClick={() => {
                if (exclusivelySelected) {
                  setEditMode(true);
                }
              }}
            />
          )}
        </Box>
      </Paper>

      {popoverAnchor && (
        <ColorPicker
          anchorElement={popoverAnchor}
          closePopover={handleClosePopover}
          onColorChange={handleColorChange}
          selectedColor={selectedColor}
          singleRow
          position="top"
        />
      )}
    </>
  );
};

const StickyNoteNodeWithProviders = (props: NodeProps<RfNodeStickyNote>) => {
  return (
    <SelectiveAspectRatioResizerCtxProvider>
      <RichTextEditorProvider fontSizeSelectorScaleModifier={props.data.textScale}>
        <StickyNoteNode {...props} />
      </RichTextEditorProvider>
    </SelectiveAspectRatioResizerCtxProvider>
  );
};

export default StickyNoteNodeWithProviders;
